import { first } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useShallowEqualSelector } from '../../../common/hooks/useShallowEqualSelector';
import { useEditable } from '../../../common/modal/EditableContext';
import {
  getUpdateIntermodalBookingEditedFormState,
  getUpdateIntermodalBookingInitialFormState,
  getUpdateIntermodalBookingIsSubmitting,
  getUpdateIntermodalBookingValidationErrors,
  setUpdateIntermodalBookingFormValue,
  type UpdateIntermodalBookingFormModel,
} from '../../../services/intermodal/updateIntermodalReducer';
import type { StoreState } from '../../../store/storeState';
import {
  type TrackerAction,
  transformUpdateIntermodalFieldToTrackerAction,
} from '../../simple-create/hooks/UseTrackingTextInput';

export interface UseIntermodalFieldProps<Key extends keyof UpdateIntermodalBookingFormModel> {
  disabled: boolean;
  editedValue: UpdateIntermodalBookingFormModel[Key] | undefined;
  initialValue: UpdateIntermodalBookingFormModel[Key] | undefined;
  onChange: (value: UpdateIntermodalBookingFormModel[Key]) => void;
  revertChange: () => void;
  trackerAction: TrackerAction;
  validationError?: string;
}

export const useUpdateIntermodalField = <Key extends keyof UpdateIntermodalBookingFormModel>(
  name: Key,
): UseIntermodalFieldProps<Key> => {
  const editable = useEditable();
  const mapState = useCallback(
    (state: StoreState) => {
      const editedFormState = getUpdateIntermodalBookingEditedFormState(state);
      const initialFormState = getUpdateIntermodalBookingInitialFormState(state);
      const validationErrors = getUpdateIntermodalBookingValidationErrors(state, name);
      const isSubmitting = getUpdateIntermodalBookingIsSubmitting(state);
      return {
        editedValue: editedFormState[name],
        initialValue: initialFormState[name],
        validationError: first(validationErrors),
        isSubmitting,
      };
    },
    [name],
  );

  const { editedValue, initialValue, validationError, isSubmitting } =
    useShallowEqualSelector(mapState);
  const disabled = isSubmitting || !editable;

  const trackerAction = transformUpdateIntermodalFieldToTrackerAction(name);

  const dispatch = useDispatch();
  const onChange = useCallback<(value: UpdateIntermodalBookingFormModel[Key]) => void>(
    newValue => {
      if (editedValue !== newValue) {
        dispatch(setUpdateIntermodalBookingFormValue({ [name]: newValue }));
      }
    },
    [editedValue, dispatch, name],
  );

  const revertChange = useCallback(
    () => () => {
      onChange(initialValue);
    },
    [initialValue, onChange],
  );

  return {
    editedValue,
    initialValue,
    onChange,
    revertChange,
    validationError,
    disabled,
    trackerAction,
  };
};
